import {
  AssetListHealthScenario,
  AssetBasicModel,
  SystemTypePpu,
  useListAssets,
  AssetsListFilterParams,
  AssetListScenarios,
} from '@customer/store/apis/assetsApi';
import { useAppDispatch, useAppSelector } from '@customer/store/hooks';
import { useCallback } from 'react';
import { AssetSidePanelTab, openSidePanel, SidePanel } from '@customer/store/slices/globalSlice';
import { FilterGroup } from '@customer/filters/types';
import useFilters from '@customer/filters/useFilters';

interface UseAnalyticsSubPageProps {
  scenario: AssetListHealthScenario;
  filterGroup?: FilterGroup;
  sidePanelTab?: AssetSidePanelTab;
  sidePanelDefaultAccordion?: string;
}

const isHealthSelfTestScenario = (
  scenario: AssetListHealthScenario,
): scenario is AssetListScenarios.HealthSelfTest => {
  return scenario === AssetListScenarios.HealthSelfTest;
};

export const useAnalyticsSubPage = <FiltersSubset extends AssetsListFilterParams>({
  scenario,
  filterGroup,
  sidePanelTab,
  sidePanelDefaultAccordion: defaultAccordion,
}: UseAnalyticsSubPageProps) => {
  const dispatch = useAppDispatch();

  const timeRange = useAppSelector(state => state.global.timeRange);

  const filters = useFilters<FiltersSubset>(filterGroup);

  const scenarioBasedProps = isHealthSelfTestScenario(scenario)
    ? { scenario, timeRange }
    : { scenario };

  const {
    assets,
    hasData,
    noDataDescription,
    countAll,
    countFilter,
    countScenarioFilter,
    isFetching,
  } = useListAssets({
    query: {
      ...filters,
      ...scenarioBasedProps,
    },
  });

  const onRowClick = useCallback(
    (data: AssetBasicModel) => {
      dispatch(
        openSidePanel({
          sidePanel: SidePanel.Asset,
          modelId: data.id.toString(),
          // Open the side panel asset config tab if dealing with PPU
          tab:
            data.hw === SystemTypePpu
              ? AssetSidePanelTab.Configuration
              : (sidePanelTab ?? AssetSidePanelTab.SelfTestSummary),
          // Only open the specified accordion for non-PPU
          defaultAccordion: data.hw === SystemTypePpu ? undefined : defaultAccordion,
        }),
      );
    },
    [dispatch, sidePanelTab, defaultAccordion],
  );

  return {
    assets,
    hasData,
    noDataDescription,
    countAll,
    countFilter,
    countScenarioFilter,
    onRowClick,
    isFetching,
  };
};

export default useAnalyticsSubPage;
